import { Flex, Text, Badge, Box, SimpleGrid, Stack, Button } from "@mantine/core";
import {
  IconBuilding,
  IconMapPin,
  IconTool,
  IconUser,
  IconShieldCheck,
  IconDownload,
} from "@tabler/icons-react";
import S3Image from "@/components/shared/S3Image";
import ProductsServicesTab from "@/components/shared/AnonymousCompanyProfile/ProductsServicesTab";
import ProfileCollapsibleSection from "@/components/shared/ProfileCollapsibleSection";
import ProfileImagesDocumentsSections from "@/components/shared/ProfileImagesDocumentsSections";
import {
  getIndustryLabel,
  getSubIndustryLabel,
  getCollaborationIntentLabel,
  getWhoAmILabel,
  getAdditionalTechInfraLabel,
  FileCategory,
  regionsData,
} from "@/components/shared/constants/general.const";
import { formatVerificationBusinessTypeLabel } from "@/pages/EmptyCompanyProfile/EmptyProfileForms/verificationDocuments";
import { displayHeadquartersText } from "@/shared/constants/signup.const";
import {
  anonymizedContactFieldValue,
  anonymizedVerificationFieldValue,
} from "@/shared/utils/anonymizedProfileDisplay";
import { registryCompanyStatusTextColor } from "@/shared/utils/registryCompanyStatusDisplay";
import { formatCompanyProfileTextForDisplay } from "@/shared/utils/capitalizeLeadingCharacter";

interface MatchedProfileTabProps {
  company: any;
  anonymousView?: boolean;
  onDownloadVerificationPdf?: () => void | Promise<void>;
  verificationPdfDownloading?: boolean;
}

export default function MatchedProfileTab({
  company,
  anonymousView = false,
  onDownloadVerificationPdf,
  verificationPdfDownloading,
}: MatchedProfileTabProps) {
  const getRegionLabel = (regionId: string): string => {
    const region = regionsData.regions.find((r) => r.id === regionId);
    return region ? region.name : regionId;
  };

  const metadata = company.externalVerificationLogs?.[0]?.metadata;

  return (
    <>
      {/* Business Overview */}
      <ProfileCollapsibleSection
        title="Business Overview"
        icon={<IconBuilding size={18} color="#ffc300" stroke={1.5} />}
        defaultOpen
      >
        <Flex direction="column" gap="lg">

          <Flex
            style={{ flex: "1 1 100%" }}
            justify="space-between"
            gap="md"
            wrap="wrap"
            w="100%"
            direction={{ base: "column", md: "row" }}
          >
            <Flex style={{ flex: "1 1 48%", minWidth: 0 }} gap="md" wrap="wrap" w={{ base: "100%", md: "auto" }}>
              <Flex direction="column" style={{ flex: "1 1 46%", minWidth: 0 }}>
                <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                  COMPANY DESCRIPTION
                </Text>
                <Text size="sm" style={{ lineHeight: 1.5, wordBreak: "break-word", overflow: "hidden", textAlign: "justify" }}>
                  {formatCompanyProfileTextForDisplay(company.description)}
                </Text>
              </Flex>

            </Flex>

            <Flex style={{ flex: "1 1 48%", minWidth: 0 }} gap="md" wrap="wrap" w={{ base: "100%", md: "auto" }}>
              <Flex direction="column" style={{ flex: "1 1 46%", minWidth: 0 }}>
                <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                  SUB DESCRIPTION
                </Text>
                <Text size="sm" style={{ lineHeight: 1.5, wordBreak: "break-word" }}>
                  {formatCompanyProfileTextForDisplay(company.subDescription)}
                </Text>
              </Flex>

              <Flex direction="column" style={{ flex: "1 1 46%", minWidth: 0 }}>
                <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                  COUNTRY
                </Text>
                <Text size="sm">{displayHeadquartersText(company.headquartersCountry)}</Text>
              </Flex>
            </Flex>
          </Flex>



          {/* Two Column Layout */}
          <Flex gap="xl" wrap="wrap" w="100%" direction={{ base: "column", sm: "row" }}>

            {/* <Flex direction="column" style={{ flex: "1 1 45%", minWidth: 0 }}>
              <Text size="xs" c="slate.6" tt="uppercase" mb="xs" fw={400}>
                YEARS OF OPERATION
              </Text>
              <Text size="sm">{getYearsOfOperation()}</Text>
            </Flex> */}

            <Flex
              style={{ flex: "1 1 100%" }}
              justify="space-between"
              gap="md"
              wrap="wrap"
              w="100%"
              direction={{ base: "column", md: "row" }}
            >
              <Flex style={{ flex: "1 1 48%", minWidth: 0 }} gap="md" wrap="wrap" w={{ base: "100%", md: "auto" }}>
                <Flex direction="column" style={{ flex: "1 1 46%", minWidth: 0 }}>
                  <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                    OPERATIONS
                  </Text>
                  <Flex gap="xs" wrap="wrap">
                    {Array.isArray(company.operations) && company.operations.length > 0 ? (
                      company.operations.map((op: string, i: number) => (
                        <Badge key={`${op}-${i}`} variant="light" color="blue" bg="#EFF6FF" c="#1D4ED8">
                          {op}
                        </Badge>
                      ))
                    ) : (
                      <Text size="sm">N/A</Text>
                    )}
                  </Flex>
                </Flex>
                <Flex direction="column" style={{ flex: "1 1 46%", minWidth: 0 }}>
                  <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                    BUSINESS STRUCTURE
                  </Text>
                  <Text size="sm">
                    {formatVerificationBusinessTypeLabel(company.additionalDetails?.verificationBusinessType)}
                  </Text>
                </Flex>
              </Flex>

              <Flex style={{ flex: "1 1 48%", minWidth: 0 }} gap="md" wrap="wrap" w={{ base: "100%", md: "auto" }}>
                <Flex direction="column" style={{ flex: "1 1 46%", minWidth: 0 }}>
                  <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                    PAID-UP CAPITAL BAND (USD)
                  </Text>
                  <Text size="sm">{company.additionalDetails?.paidupCapitalBand?.trim() || "N/A"}</Text>
                </Flex>

                <Flex direction="column" style={{ flex: "1 1 46%", minWidth: 0 }}>
                  <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                    TURNOVER BAND (USD)
                  </Text>
                  <Text size="sm">{company.additionalDetails?.turnoverBand?.trim() || "N/A"}</Text>
                </Flex>
              </Flex>
            </Flex>

            <Flex
              style={{ flex: "1 1 100%" }}
              justify="space-between"
              gap="md"
              wrap="wrap"
              w="100%"
              direction={{ base: "column", md: "row" }}
            >
              <Flex style={{ flex: "1 1 48%", minWidth: 0 }} gap="md" wrap="wrap" w={{ base: "100%", md: "auto" }}>
                <Flex direction="column" style={{ flex: "1 1 46%", minWidth: 0 }}>
                  <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                    INDUSTRY CATEGORY
                  </Text>
                  <Text size="sm">
                    {Array.isArray(company.industryCategory) && company.industryCategory.length > 0
                      ? company.industryCategory.map((c: string) => getIndustryLabel(c)).join(", ")
                      : "N/A"}
                  </Text>
                </Flex>

                <Flex direction="column" style={{ flex: "1 1 46%", minWidth: 0 }}>
                  <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                    SUB INDUSTRY
                  </Text>
                  <Text size="sm">
                    {Array.isArray(company.subIndustry) && company.subIndustry.length > 0
                      ? company.subIndustry.map((sub: string) => getSubIndustryLabel(sub)).join(", ")
                      : "N/A"}
                  </Text>
                </Flex>
              </Flex>

              <Flex style={{ flex: "1 1 48%", minWidth: 0 }} gap="md" wrap="wrap" w={{ base: "100%", md: "auto" }}>
                <Flex direction="column" style={{ flex: "1 1 46%", minWidth: 0 }}>
                  <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                    WHO AM I
                  </Text>
                  <Text size="sm">
                    {Array.isArray(company.whoAmI) && company.whoAmI.length > 0
                      ? company.whoAmI.map((role: string) => getWhoAmILabel(role)).join(", ")
                      : "N/A"}
                  </Text>
                </Flex>

                <Flex direction="column" style={{ flex: "1 1 46%", minWidth: 0 }}>
                  <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                    COLLABORATION INTENT
                  </Text>
                  <Text size="sm">
                    {Array.isArray(company.collaborationIntent) && company.collaborationIntent.length > 0
                      ? company.collaborationIntent.map((intent: string) => getCollaborationIntentLabel(intent)).join(", ")
                      : "N/A"}
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </Flex>

          {/* Company Gallery */}
          {company.images &&
            company.images.filter((img: any) => img.category === FileCategory.COMPANY_IMAGE).length > 0 && (
              <Box>
                <Text size="xs" c="slate.6" tt="uppercase" mb="sm" fw={400}>
                  COMPANY GALLERY
                </Text>
                <Flex gap="sm" wrap="wrap">
                  {company.images
                    .filter((img: any) => img.category === FileCategory.COMPANY_IMAGE)
                    .map((image: any, index: number) => (
                      <Box key={index} style={{ width: 100, height: 100 }}>
                        <S3Image
                          fileId={image.id}
                          alt={`Company image ${index + 1}`}
                          radius="lg"
                          fit="cover"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            border: "1px solid #E5E7EB",
                          }}
                        />
                      </Box>
                    ))}
                </Flex>
                <Text size="xs" c="slate.6" mt="xs">
                  Facility photos and product images visible after verification
                </Text>
              </Box>
            )}
        </Flex>
      </ProfileCollapsibleSection>

      {/* Products & Services */}
      <ProductsServicesTab company={company} isMatchedView={true} />

      {/* Capabilities & Offerings */}
      <ProfileCollapsibleSection
        title="Capabilities & Offerings"
        icon={<IconTool size={18} color="#ffc300" stroke={1.5} style={{ transform: "scaleX(-1)" }} />}
      >
        <Flex direction="column" gap="lg">
          {/* Core Capabilities */}
          <Box>
            <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
              CORE CAPABILITIES
            </Text>
            <Flex gap="xs" wrap="wrap">
              {(company.coreCapabilities || []).map((capability: string, index: number) => (
                <Badge key={index} style={{ backgroundColor: "#04305c", color: "white" }} py={14} px={12} size="md" fw={500}>
                  {capability}
                </Badge>
              ))}
              {(!company.coreCapabilities || company.coreCapabilities.length === 0) && (
                <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                  N/A
                </Text>
              )}
            </Flex>
          </Box>

          {/* Secondary Capabilities */}
          {company.secondaryCapabilities && company.secondaryCapabilities.length > 0 && (
            <Box>
              <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                SECONDARY CAPABILITIES
              </Text>
              <Flex gap="xs" wrap="wrap">
                {company.secondaryCapabilities.map((capability: string, index: number) => (
                  <Badge key={index} style={{ backgroundColor: "#04305c", color: "white" }} py={14} px={12} size="md" fw={500}>
                    {capability}
                  </Badge>
                ))}
              </Flex>
            </Box>
          )}

          {/* Key Differentiators */}
          {company.keyDifferentiators && (
            <Box>
              <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                KEY DIFFERENTIATORS
              </Text>
              <Text size="sm">{company.keyDifferentiators}</Text>
            </Box>
          )}

          {/* Technology & Infrastructure */}
          <Box>
            <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
              TECHNOLOGY & INFRASTRUCTURE
            </Text>
            <Flex gap="xs" wrap="wrap">
              {(company.technologies || []).map((tech: string, index: number) => (
                <Badge key={index} style={{ backgroundColor: "#04305c", color: "white" }} py={14} px={12} size="md" fw={500}>
                  {tech}
                </Badge>
              ))}
              {(!company.technologies || company.technologies.length === 0) && (
                <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                  N/A
                </Text>
              )}
            </Flex>
          </Box>

          {/* Additional Technology & Infrastructure */}
          {company.additionalTechInfrastructure && company.additionalTechInfrastructure.length > 0 && (
            <Box>
              <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                ADDITIONAL TECHNOLOGY & INFRASTRUCTURE
              </Text>
              <Flex gap="xs" wrap="wrap">
                {company.additionalTechInfrastructure.map((item: string, index: number) => (
                  <Badge key={index} style={{ backgroundColor: "#04305c", color: "white" }} py={14} px={12} size="md" fw={500}>
                    {getAdditionalTechInfraLabel(item)}
                  </Badge>
                ))}
              </Flex>
            </Box>
          )}
        </Flex>
      </ProfileCollapsibleSection>

      {/* Geography & Markets */}
      <ProfileCollapsibleSection
        title="Geography & Markets"
        icon={<IconMapPin size={18} color="#ffc300" stroke={1.5} />}
      >
        <Flex gap="xl" wrap="wrap" w="100%" direction={{ base: "column", sm: "row" }}>
          {/* Operating GEOGRAPHIES */}
          <Flex direction="column" style={{ flex: "1 1 45%", minWidth: 0 }} w={{ base: "100%", sm: "auto" }}>
            <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
              OPERATING GEOGRAPHIES
            </Text>
            <Flex gap="xs" wrap="wrap">
              {(company.operatingCountries || [])
                .map((country: string) => displayHeadquartersText(country))
                .filter((label: string) => label !== "N/A")
                .map((country: string, index: number) => (
                  <Badge
                    key={index}
                    style={{ backgroundColor: "#04305c", color: "white" }}
                    py={14}
                    px={12}
                    size="md"
                    fw={500}
                  >
                    {country}
                  </Badge>
                ))}
              {(!company.operatingCountries ||
                company.operatingCountries.length === 0 ||
                (company.operatingCountries || []).every(
                  (c: string) => displayHeadquartersText(c) === "N/A",
                )) && (
                  <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                    N/A
                  </Text>
                )}
            </Flex>
          </Flex>

          {/* Preferred Collaboration Regions */}
          <Flex direction="column" style={{ flex: "1 1 45%", minWidth: 0 }} w={{ base: "100%", sm: "auto" }}>
            <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
              PREFERRED COLLABORATION REGIONS
            </Text>
            <Flex gap="xs" wrap="wrap">
              {(company.preferredCollaborationRegions || []).map((region: string, index: number) => (
                <Badge key={index} style={{ backgroundColor: "#04305c", color: "white" }} py={14} px={12} size="md" fw={500}>
                  {getRegionLabel(region)}
                </Badge>
              ))}
              {(!company.preferredCollaborationRegions || company.preferredCollaborationRegions.length === 0) && (
                <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                  N/A
                </Text>
              )}
            </Flex>
          </Flex>
        </Flex>
      </ProfileCollapsibleSection>

      {/* Contact Person Details */}
      <ProfileCollapsibleSection
        title="Contact Person"
        icon={<IconUser size={18} color="#ffc300" stroke={1.5} />}
      >
        <Box
          pt="md"
          pb="md"
          style={{
            borderTop: "1px solid #E5E7EB",
            borderBottom: "1px solid #E5E7EB",
          }}
        >
          <Flex gap="md" wrap="wrap" w="100%" direction={{ base: "column", sm: "row" }}>
            <Flex direction="column" style={{ flex: "1 1 220px", minWidth: 0 }} w={{ base: "100%", sm: "auto" }}>
              <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                NAME
              </Text>
              <Text size="sm">{anonymizedContactFieldValue(company.user?.name, anonymousView)}</Text>
            </Flex>
            <Flex direction="column" style={{ flex: "1 1 220px", minWidth: 0 }} w={{ base: "100%", sm: "auto" }}>
              <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                PHONE NUMBER
              </Text>
              <Text size="sm">{anonymizedContactFieldValue(company.user?.phoneNumber, anonymousView)}</Text>
            </Flex>
            <Flex direction="column" style={{ flex: "1 1 220px", minWidth: 0 }} w={{ base: "100%", sm: "auto" }}>
              <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                DESIGNATION
              </Text>
              <Text size="sm">{anonymizedContactFieldValue(company.user?.designation, anonymousView)}</Text>
            </Flex>
          </Flex>
        </Box>
      </ProfileCollapsibleSection>

      {/* System Verification Details */}
      {metadata && (
        <ProfileCollapsibleSection
          title="System Verification Details"
          icon={<IconShieldCheck size={18} color="#ffc300" stroke={1.5} />}
          headerRight={
            onDownloadVerificationPdf ? (
              <Button
                variant="white"
                bg="#FFC300"
                color="dark"
                size="compact-sm"
                leftSection={<IconDownload size={16} />}
                onClick={() => void onDownloadVerificationPdf()}
                loading={verificationPdfDownloading}
                disabled={verificationPdfDownloading}
                style={{ flexShrink: 0 }}
              >
                Download
              </Button>
            ) : undefined
          }
        >
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing={{ base: "lg", sm: "xl" }} verticalSpacing="lg">
            <Stack gap="xs">
              <Text size="md" c="slate.8" tt="uppercase" fw={600}>
                COMPANY NAME
              </Text>
              <Text size="sm">
                {anonymizedVerificationFieldValue(metadata.companyName, anonymousView)}
              </Text>
            </Stack>
            <Stack gap="xs">
              <Text size="md" c="slate.8" tt="uppercase" fw={600}>
                COMPANY ADDRESS
              </Text>
              <Text size="sm">
                {anonymizedVerificationFieldValue(metadata.companyAddress, anonymousView)}
              </Text>
            </Stack>
            <Stack gap="xs">
              <Text size="md" c="slate.8" tt="uppercase" fw={600}>
                DATE OF REGISTRATION OF CERTIFICATE
              </Text>
              <Text size="sm">
                {anonymizedVerificationFieldValue(metadata.dateOfIncorporation, anonymousView)}
              </Text>
            </Stack>
            <Stack gap="xs">
              <Text size="md" c="slate.8" tt="uppercase" fw={600}>
                REGISTRATION NUMBER
              </Text>
              <Text size="sm">
                {anonymizedVerificationFieldValue(metadata.registrationNumber, anonymousView)}
              </Text>
            </Stack>
            <Stack gap="xs">
              <Text size="md" c="slate.8" tt="uppercase" fw={600}>
                COMPANY TYPE
              </Text>
              <Text size="sm">
                {anonymizedVerificationFieldValue(metadata.companyType, anonymousView)}
              </Text>
            </Stack>
            <Stack gap="xs">
              <Text size="md" c="slate.8" tt="uppercase" fw={600}>
                COMPANY STATUS
              </Text>
              <Flex align="center" wrap="wrap">
                <Text
                  size="sm"
                  fw={anonymousView ? 400 : 500}
                  c={registryCompanyStatusTextColor((metadata as any)?.companyStatus, anonymousView)}
                >
                  {anonymizedVerificationFieldValue(metadata.companyStatus, anonymousView)}
                </Text>
              </Flex>
            </Stack>
          </SimpleGrid>
        </ProfileCollapsibleSection>
      )}

      <ProfileImagesDocumentsSections
        company={company}
        mode="view"
        isMatchedView
        refreshCompany={async () => { }}
        activeTab="images"
      />
    </>
  );
}
